import { Flex, Icon, Stack, Text, VStack } from "@chakra-ui/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { effectiveDeadlineIso } from "../../utils/effectiveDeadline";
import { FiMessageSquare } from "react-icons/fi";
import {
  PortalSourcingEventDetailDto,
  useGetPortalAnnouncementsQuery,
  useGetPortalSeV2QuestionsQuery,
} from "../../../../../autogen/bff-api";
import { AnnouncementCard } from "../../../../../common/components/communication/AnnouncementCard";
import { useApiError } from "../../../../../common/errors/useApiError";
import { useAppSelector } from "../../../../../common/redux/hooks";
import { SectionCard } from "../SectionCard";
import { SupplierAskQuestionForm } from "./SupplierAskQuestionForm";
import { SupplierQuestionThread } from "./SupplierQuestionThread";

export const EventCommunicationSection = ({
  event,
}: {
  event: PortalSourcingEventDetailDto;
}) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const eventId = event.id;
  const authState = useAppSelector((state) => state.authState.state);
  const isLoggedIn = authState?.type === "LoggedInWithOrgContext";

  const { data: announcementsData, error: announcementsError } =
    useGetPortalAnnouncementsQuery({ eventId }, { skip: !isLoggedIn });
  const { data: questionsData, error: questionsError } =
    useGetPortalSeV2QuestionsQuery({ eventId }, { skip: !isLoggedIn });

  useEffect(() => {
    if (announcementsError) displayer.triggerSoft(announcementsError);
  }, [announcementsError, displayer]);
  useEffect(() => {
    if (questionsError) displayer.triggerSoft(questionsError);
  }, [questionsError, displayer]);

  const announcements = announcementsData?.announcements ?? [];
  const myQuestions = questionsData?.questions ?? [];

  // A question the supplier asked that was published already shows as their own thread (with the answer),
  // so suppress the public announcement derived from it — otherwise it reads as a duplicate. Other suppliers,
  // who don't have the thread, still see it.
  const ownPublishedAnnouncementIds = new Set(
    myQuestions
      .map((q) => q.publishedAnnouncementId)
      .filter((id): id is string => !!id),
  );
  const feedAnnouncements = announcements.filter(
    (a) => !ownPublishedAnnouncementIds.has(a.id),
  );
  const count = feedAnnouncements.length + myQuestions.length;

  // One chronological feed (oldest → newest). Each question stays grouped with its answer (the thread is one item),
  // anchored at the time the question was asked.
  const feed = [
    ...feedAnnouncements.map((a) => ({
      at: a.createdAt,
      el: <AnnouncementCard key={`ann-${a.id}`} announcement={a} />,
    })),
    ...myQuestions.map((q) => ({
      at: q.createdAt,
      el: <SupplierQuestionThread key={`q-${q.id}`} question={q} />,
    })),
  ]
    .sort((x, y) => x.at.localeCompare(y.at))
    .map((item) => item.el);

  return (
    <SectionCard
      icon={FiMessageSquare}
      title={t("Questions and answers")}
      count={count > 0 ? count : undefined}
    >
      <Stack spacing="4">
        {feed}
        {isLoggedIn && count === 0 && (
          <VStack spacing="2" py="4" textAlign="center">
            <Flex
              w="10"
              h="10"
              rounded="lg"
              align="center"
              justify="center"
              bg="smPrimarySofter"
              color="smChipTealFg"
            >
              <Icon as={FiMessageSquare} boxSize="5" />
            </Flex>
            <Text fontSize="sm" fontWeight="semibold" color="smText">
              {t("No questions yet")}
            </Text>
            <Text fontSize="sm" color="smMuted" maxW="sm">
              {t(
                "Ask the buyer if anything in the tender is unclear. Answers may be published to all tenderers.",
              )}
            </Text>
          </VStack>
        )}
        {isLoggedIn ? (
          <SupplierAskQuestionForm
            eventId={eventId}
            questionDeadline={event.questionDeadline ?? effectiveDeadlineIso(event) ?? ""}
          />
        ) : (
          <Text fontSize="sm" color="smMuted">
            {t("Log in to ask the buyer a question.")}
          </Text>
        )}
      </Stack>
    </SectionCard>
  );
};
